import {
  View,
  StyleSheet,
  TouchableOpacity,
  BackHandler,
  Keyboard,
  Platform,
} from 'react-native';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  interpolate,
  SharedValue,
} from 'react-native-reanimated';
import ProductList from './components/ProductList';
import { useAppSelector } from '../../redux/helper';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import { useDispatch } from 'react-redux';
import { ClearCart, clearCartOrderData } from '../../redux/cartSlice';
import { setUpdatingCartitems } from '../../redux/cartSlice';
import { clearOrderDetails } from '../../redux/orderDetailsSlice';
import { resetCheckoutPaymentIntent } from '../../redux/checkoutSlice';
import CartIcon from '../../components/Reusables/CartIcon';
import ClearBasketActions from '../../components/sale/ClearBasketActions';
import Typography from '../../components/Reusables/Typography';
import CustomBottomSheet from '../../components/Reusables/bottomSheet/CustomBottomSheet';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { fs, s, v, borderWidth } from '../../utils/libs';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { isTablet } from 'react-native-device-info';
import { useNavigation as useNavigationContext } from '../../Context/NavigationContext';
import { ModalContext } from '../../Context/modalContext';
import { GTFonts, useLayoutSize, useTheme } from '../../theme';
import FilterModal from '../../components/Modals/FilterModal';
import { SettingsDetails } from '../../redux/SettingsSlice';
import { isEmployeeAdminRole } from '../../utils/roles';

// Define a type for menu items
interface FloatingMenuItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
  backgroundColor: string;
}

const ProductsScreen = ({ navigation }: { navigation: any }) => {
  const { theme } = useTheme();
  const { useSidebarNav: useSidebar } = useLayoutSize();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const fromCheckoutBottomSheetModalRef = useRef<BottomSheetModal>(null as any);
  const { clearOrderDetailsData } = useNavigationContext();
  const { setInput, setSelectedCustomerId, setAssignedStaffId } =
    useContext(ModalContext);
  const employeedata = useAppSelector(
    state => state.auth.inApp.account.employeeInfo,
  );
  const dispatch = useDispatch();
  const route = useRoute();
  const cart = useAppSelector(state => state?.cart);
  const { updatingCartitems, cartTotalQuantity } = cart;

  // Add animation for cart position to prevent overlap
  const cartAnimation = useSharedValue(0);
  const settings = useAppSelector(SettingsDetails);
  // Keyboard height tracking for floating cart
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const keyboardHeightShared = useSharedValue(0);

  // Get employee role from state
  const employeeRole = useAppSelector(
    state => state.auth.inApp.account?.employeeInfo,
  );
  // Keyboard listeners for floating cart positioning
  useEffect(() => {
    const keyboardWillShowListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      e => {
        setKeyboardHeight(e.endCoordinates.height);
        keyboardHeightShared.value = e.endCoordinates.height;
      },
    );
    const keyboardWillHideListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => {
        setKeyboardHeight(0);
        keyboardHeightShared.value = 0;
      },
    );

    return () => {
      keyboardWillShowListener.remove();
      keyboardWillHideListener.remove();
    };
  }, []);

  console.log('ProductsScreenroute is', route);
  useFocusEffect(
    useCallback(() => {
      const backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        goBack,
      );
      return () => {
        backHandler.remove();
      };
    }, []),
  );

  useEffect(() => {
    const params = route?.params as { from?: string } | undefined;
    console.log('params', params, 'updatingCartitems', updatingCartitems);
    if (params?.from === 'Checkout' && updatingCartitems) {
      if (settings?.showIncompleteOrderPopup !== false) {
        fromCheckoutBottomSheetModalRef.current?.present();
        navigation.setParams({ from: undefined });
      } else {
        navigation.setParams({ from: undefined });
      }
    }
  }, [
    route?.params,
    updatingCartitems,
    navigation,
    settings?.showIncompleteOrderPopup,
  ]);

  const goBack = useCallback(() => {
    navigation.popTo('DrawerNavigation', {
      screen: 'Home',
    });
    return true;
  }, [navigation]);

  const clearCart = useCallback(() => {
    dispatch(ClearCart());
    dispatch(clearCartOrderData());
    dispatch(setUpdatingCartitems(false));
    dispatch(clearOrderDetails());
    dispatch(resetCheckoutPaymentIntent());
    setInput('');
    setSelectedCustomerId(null);
    setAssignedStaffId(null);
    closeModal();
    clearOrderDetailsData();
  }, [
    dispatch,
    clearOrderDetailsData,
    setInput,
    setSelectedCustomerId,
    setAssignedStaffId,
  ]);

  const closeModal = useCallback(() => {
    fromCheckoutBottomSheetModalRef.current?.dismiss();
  }, []);

  const handleSheetChanges = useCallback((index: number) => {
    console.log('handleSheetChanges', index);
    if (index === -1) {
      fromCheckoutBottomSheetModalRef.current?.dismiss();
    }
  }, []);

  const renderCartIcon = useCallback(() => {
    if (cartTotalQuantity > 0) {
      return <CartIcon size={isTablet() ? 26 : 22} />;
    }
    return null;
  }, [cartTotalQuantity]);

  // Memoize menu items to prevent unnecessary re-renders
  const menuItems = useMemo((): FloatingMenuItem[] => {
    const items: FloatingMenuItem[] = [];

    // Only add the "New Product" option for admin roles (owner, manager, supervisor)
    if (isEmployeeAdminRole(employeeRole?.bp_employee_role)) {
      items.push({
        id: 'addProduct',
        icon: <Ionicons name="add-circle" size={25} color="white" />,
        label: 'New Product',
        onPress: () => navigation.navigate('NewProduct'),
        backgroundColor: theme.colors.primary,
      });
    }

    return items;
  }, [employeeRole?.bp_employee_role, navigation]);

  const cartStyle = useAnimatedStyle(() => {
    const menuItemsCount = menuItems.length;
    const moveUpDistance = menuItemsCount * 55;

    // Add keyboard offset - move cart up when keyboard is visible
    const keyboardOffset =
      keyboardHeightShared.value > 0 ? keyboardHeightShared.value : 0;

    return {
      transform: [
        {
          translateY:
            interpolate(cartAnimation.value, [0, 1], [0, -moveUpDistance]) -
            keyboardOffset,
        },
      ],
    };
  });

  return (
    <View style={styles.container}>
      <ProductList />
      {!useSidebar && cartTotalQuantity > 0 ? (
        <Animated.View
          style={[
            styles.floatingCart,
            {
              backgroundColor: theme.colors.success,
              right: s(isTablet() ? 10 : 21),
              bottom: v(isTablet() ? 16 : 16),
            },
            cartStyle,
          ]}>
          <TouchableOpacity onPress={() => navigation.navigate('Cart')}>
            {renderCartIcon()}
          </TouchableOpacity>
        </Animated.View>
      ) : null}

      <CustomBottomSheet
        bottomSheetModalRef={fromCheckoutBottomSheetModalRef}
        snapPoints={['40%']}
        enableDynamicSizing={false}
        handleSheetChanges={handleSheetChanges}
        constrainWidth
        footer={
          <ClearBasketActions onClear={clearCart} onContinue={closeModal} />
        }>
        <View style={styles.bottomSheet}>
          <View style={styles.bottomSheetContainer}>
            <Typography style={styles.bottomSheetTitle}>
              Clear Basket?
            </Typography>
            <Typography style={styles.bottomSheetinfoText}>
              Hi {employeedata?.bp_employee_name}, Since this order is not
              completed, it will be automatically saved. To process another
              order, please clear the basket.
            </Typography>
          </View>
        </View>
      </CustomBottomSheet>
      {/* ProductList.web renders FilterModal; avoid double mount on web and native sidebar (tablet). */}
      {Platform.OS !== 'web' && !useSidebar && <FilterModal />}
    </View>
  );
};

export default React.memo(ProductsScreen);

// Create themed styles function (like HomeScreen)
const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      margin: 0,
      flex: 1,
      width: '100%',
      backgroundColor: theme.colors.background, // Use themed background like HomeScreen
    },
    top: {
      maxWidth: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginHorizontal: s(15),
    },
    search: {
      width: '85%',
    },
    icons: {
      width: '14%',
      justifyContent: 'center',
      alignItems: 'center',
      height: v(37),
      alignSelf: 'center',
      marginLeft: s(8),
      borderWidth: borderWidth(0.5),
      borderColor: theme.colors.primaryDark,
      borderRadius: s(5),
      backgroundColor: theme.colors.surface,
    },
    iconspressin: {
      width: '14%',
      justifyContent: 'center',
      alignItems: 'center',
      height: v(35),
      alignSelf: 'center',
      marginLeft: s(8),
      borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderColor: theme.colors.surface,
      color: theme.colors.textInverse,
      borderRadius: s(5),
      backgroundColor: theme.colors.primaryDark,
    },
    icon: {
      height: v(28),
    },
    floatingCart: {
      position: 'absolute',
      height: v(46),
      width: s(46),
      padding: 0,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: s(8),
      elevation: 8,
    },

    circularButton: {
      position: 'absolute',
      height: v(48),
      width: s(48),
      bottom: v(6),
      right: s(15),
      borderRadius: s(25),
      elevation: 8,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.colors.primary, // Changed from theme.colors.secondary;
      zIndex: 1,
    },

    bottomSheet: {
      flex: 1,
      marginTop: v(15),
      alignItems: 'center',
      backgroundColor: theme.colors.surface,
    },
    bottomSheetContainer: {
      flex: 1,
      alignItems: 'center',
    },
    bottomSheetTitle: {
      fontSize: isTablet() ? 28 : 24,
      lineHeight: 30,
      fontFamily: GTFonts.bold,
      marginBottom: v(10),
      color: theme.colors.textDark,
    },
    bottomSheetinfoText: {
      fontSize: fs(17),
      lineHeight: fs(20),
      textAlign: 'center',
      fontFamily: GTFonts.regular,
      marginTop: v(10),
      marginHorizontal: s(15),
      color: theme.colors.text,
    },
    bottomSheetButton: {
      marginBottom: v(50),
    },
    cancel: {
      position: 'absolute',
      padding: s(8),
      right: s(8),
      top: 0,
    },
  });

const MenuItem = React.memo(
  ({
    item,
    index,
    sharedAnimation,
    theme,
  }: {
    item: FloatingMenuItem;
    index: number;
    sharedAnimation: SharedValue<number>;
    theme: any;
  }) => {
    const menuItemStyles = useMemo(() => createMenuItemStyles(theme), [theme]);
    const animatedStyle = useAnimatedStyle(() => {
      const offScreenTranslateX = 200;
      const offScreenTranslateY = 200;

      return {
        transform: [
          {
            translateX: interpolate(
              sharedAnimation.value,
              [0, 1],
              [offScreenTranslateX, 0],
            ),
          },
          {
            translateY: interpolate(
              sharedAnimation.value,
              [0, 1],
              [offScreenTranslateY, -50 * (index + 1)],
            ),
          },
          {
            rotate: `${interpolate(
              sharedAnimation.value,
              [0, 0.1, 0.8, 1],
              [45, 20, 10, 0],
            )}deg`,
          },
        ],
        opacity: interpolate(
          sharedAnimation.value,
          [0, 0.1, 0.9, 1],
          [0, 0, 0, 1],
        ),
        zIndex: interpolate(sharedAnimation.value, [0, 0.1, 1], [-1, 1, 1]),
      };
    });

    return (
      <Animated.View style={[menuItemStyles.floatingButton, animatedStyle]}>
        <TouchableOpacity
          style={[
            menuItemStyles.pillButton,
            { backgroundColor: item.backgroundColor },
          ]}
          onPress={item.onPress}>
          {item.icon}
          <Typography style={menuItemStyles.pillButtonText}>
            {item.label}
          </Typography>
        </TouchableOpacity>
      </Animated.View>
    );
  },
);

MenuItem.displayName = 'MenuItem';

// Create menu item styles function
const createMenuItemStyles = (theme: any) =>
  StyleSheet.create({
    floatingButton: {
      position: 'absolute',
      bottom: 10,
      right: 15,
      justifyContent: 'center',
      alignItems: 'flex-end',
      zIndex: 1,
      marginBottom: 10,
    },
    pillButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 10,
      borderRadius: 25,
      minWidth: 40,
      height: 38,
      elevation: 8,
      paddingBottom: 1,
    },
    pillButtonText: {
      color: 'white',
      marginLeft: 6,
      fontSize: 14,
      fontWeight: '500',
    },
  });
